import {
  Document, Page, View, Text, Image,
  StyleSheet, Svg, Rect, Defs, LinearGradient, Stop,
} from '@react-pdf/renderer';

const NAVY  = '#1e3a5f';
const BLUE  = '#3b82f6';
const SLATE = '#64748b';
const MUTED = '#94a3b8';
const LIGHT = '#f8fafc';
const BORDER = '#e2e8f0';
const RED   = '#dc2626';

const fmt = (val) =>
  val != null
    ? `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const isOverdue = (inv) =>
  !inv.isPaid && inv.dueDate && new Date(inv.dueDate) < new Date(new Date().toDateString());

const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 10,
    paddingRight: 10,
    color: '#1a202c',
  },
  // ── Header ──
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  logo: { maxHeight: 52, maxWidth: 170 },
  companyName: { fontSize: 18, fontFamily: 'Helvetica-Bold', color: NAVY },
  titleBlock: { alignItems: 'flex-end' },
  titleWord: { fontSize: 30, fontFamily: 'Helvetica-Bold', color: NAVY, letterSpacing: 3 },
  docNumber: { fontSize: 11, color: SLATE, marginTop: 4 },
  // ── Meta ──
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 14, marginBottom: 22 },
  billTo: { flex: 1 },
  sectionLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: MUTED, letterSpacing: 1.5, marginBottom: 5 },
  customerName: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#0f172a' },
  customerEmail: { fontSize: 9, color: SLATE, marginTop: 3 },
  detailsCol: { alignItems: 'flex-end' },
  detailRow: { flexDirection: 'row', marginBottom: 5 },
  detailLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: MUTED, letterSpacing: 0.5, width: 78, textAlign: 'right', marginRight: 10 },
  detailValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#1e293b', width: 115, textAlign: 'right' },
  valOverdue: { color: RED },
  statusChip: { fontSize: 7, fontFamily: 'Helvetica-Bold', letterSpacing: 1, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 99 },
  statusPaid:    { backgroundColor: '#d1fae5', color: '#065f46' },
  statusOpen:    { backgroundColor: '#fef3c7', color: '#92400e' },
  statusOverdue: { backgroundColor: '#fee2e2', color: '#991b1b' },
  // ── Table ──
  tableHeader: { flexDirection: 'row', backgroundColor: NAVY, paddingVertical: 7, paddingHorizontal: 10 },
  thCell: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#e2e8f0', letterSpacing: 0.8 },
  tableRow: { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 10, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  tableRowEven: { backgroundColor: LIGHT },
  tdCell: { fontSize: 9, color: '#374151' },
  tdMuted: { color: SLATE, fontSize: 8.5 },
  tdNum: { textAlign: 'right', fontFamily: 'Helvetica-Bold' },
  // col widths
  colDesc: { flex: 1 },
  colItem: { width: 72 },
  colQty:  { width: 36 },
  colRate: { width: 56 },
  colAmt:  { width: 64 },
  // ── Totals ──
  totalsSection: { alignItems: 'flex-end', marginTop: 4, marginBottom: 20 },
  totalsBox: { width: 230 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  tLabel: { fontSize: 9, color: SLATE },
  tValue: { fontSize: 9, color: SLATE },
  totalRowBold: { backgroundColor: LIGHT },
  tLabelBold: { fontFamily: 'Helvetica-Bold', color: '#1e293b', fontSize: 10 },
  tValueBold: { fontFamily: 'Helvetica-Bold', color: '#1e293b', fontSize: 10 },
  totalRowBalance: { backgroundColor: NAVY },
  tLabelBalance: { fontFamily: 'Helvetica-Bold', color: '#fff', fontSize: 11 },
  tValueBalance: { fontFamily: 'Helvetica-Bold', color: '#fff', fontSize: 11 },
  totalRowPaid: { backgroundColor: '#064e3b' },
  tLabelPaid: { fontFamily: 'Helvetica-Bold', color: '#a7f3d0', fontSize: 10 },
  tValuePaid: { fontFamily: 'Helvetica-Bold', color: '#a7f3d0', fontSize: 10 },
  // ── Memo ──
  memo: { borderTopWidth: 1, borderTopColor: '#f1f5f9', paddingTop: 12, marginBottom: 16 },
  memoText: { fontSize: 9, color: SLATE, marginTop: 4 },
  // ── Footer ──
  footer: { borderTopWidth: 1, borderTopColor: BORDER, paddingTop: 10, flexDirection: 'row', justifyContent: 'space-between', marginTop: 20 },
  footerMsg: { fontSize: 9, color: SLATE, fontFamily: 'Helvetica-Oblique' },
  footerMeta: { fontSize: 8, color: MUTED },
  // ── Watermark ──
  watermark: {
    position: 'absolute', top: 300, left: 60,
    fontSize: 80, fontFamily: 'Helvetica-Bold',
    color: 'rgba(16,185,129,0.07)',
    transform: 'rotate(-35deg)',
  },
});

export default function InvoicePDF({ invoice, lineItems, companyName, logoUrl }) {
  const paid    = !!invoice.isPaid;
  const overdue = isOverdue(invoice);
  const subtotal = lineItems.reduce((sum, li) => sum + (Number(li.amount) || 0), 0);

  return (
    <Document title={`Invoice ${invoice.docNumber ?? ''}`} author={companyName}>
      <Page size="A4" style={s.page}>

        {paid && <Text style={s.watermark}>PAID</Text>}

        {/* Header */}
        <View style={s.header}>
          <View>
            {logoUrl
              ? <Image src={logoUrl} style={s.logo} />
              : <Text style={s.companyName}>{companyName}</Text>
            }
          </View>
          <View style={s.titleBlock}>
            <Text style={s.titleWord}>INVOICE</Text>
            <Text style={s.docNumber}>#{invoice.docNumber}</Text>
          </View>
        </View>

        {/* Gradient divider */}
        <Svg height={3} width="100%" style={{ marginBottom: 0 }}>
          <Defs>
            <LinearGradient id="hdr" x1="0" y1="0" x2="1" y2="0">
              <Stop offset="0"   stopColor={NAVY} />
              <Stop offset="0.6" stopColor={BLUE} />
              <Stop offset="1"   stopColor="#93c5fd" />
            </LinearGradient>
          </Defs>
          <Rect x={0} y={0} width="100%" height={3} fill="url(#hdr)" />
        </Svg>

        {/* Bill-to + Details */}
        <View style={s.metaRow}>
          <View style={s.billTo}>
            <Text style={s.sectionLabel}>BILL TO</Text>
            <Text style={s.customerName}>{invoice.customerDisplayName || '—'}</Text>
            {!!invoice.billEmail && <Text style={s.customerEmail}>{invoice.billEmail}</Text>}
          </View>
          <View style={s.detailsCol}>
            <View style={s.detailRow}>
              <Text style={s.detailLabel}>INVOICE DATE</Text>
              <Text style={s.detailValue}>{fmtDate(invoice.txnDate)}</Text>
            </View>
            <View style={s.detailRow}>
              <Text style={s.detailLabel}>DUE DATE</Text>
              <Text style={[s.detailValue, overdue ? s.valOverdue : {}]}>{fmtDate(invoice.dueDate)}</Text>
            </View>
            {!!invoice.poNumber && (
              <View style={s.detailRow}>
                <Text style={s.detailLabel}>P.O. NUMBER</Text>
                <Text style={s.detailValue}>{invoice.poNumber}</Text>
              </View>
            )}
            <View style={s.detailRow}>
              <Text style={s.detailLabel}>STATUS</Text>
              <View>
                <Text style={[s.statusChip, paid ? s.statusPaid : overdue ? s.statusOverdue : s.statusOpen]}>
                  {paid ? 'PAID' : overdue ? 'OVERDUE' : 'OPEN'}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Table header */}
        <View style={s.tableHeader}>
          <Text style={[s.thCell, s.colDesc]}>DESCRIPTION</Text>
          <Text style={[s.thCell, s.colItem]}>ITEM / SERVICE</Text>
          <Text style={[s.thCell, s.colQty, { textAlign: 'right' }]}>QTY</Text>
          <Text style={[s.thCell, s.colRate, { textAlign: 'right' }]}>RATE</Text>
          <Text style={[s.thCell, s.colAmt,  { textAlign: 'right' }]}>AMOUNT</Text>
        </View>

        {/* Line items */}
        {lineItems.length === 0 && (
          <View style={[s.tableRow]}>
            <Text style={[s.tdCell, { flex: 1, textAlign: 'center', color: MUTED, fontFamily: 'Helvetica-Oblique' }]}>
              No line items on record.
            </Text>
          </View>
        )}
        {lineItems.map((li, idx) => (
          <View key={li.lineNum ?? idx} style={[s.tableRow, idx % 2 === 1 ? s.tableRowEven : {}]} wrap={false}>
            <Text style={[s.tdCell, s.colDesc]}>{li.description || '—'}</Text>
            <Text style={[s.tdCell, s.tdMuted, s.colItem]}>{li.itemName || '—'}</Text>
            <Text style={[s.tdCell, s.tdNum, s.colQty]}>
              {li.quantity != null ? Number(li.quantity).toLocaleString() : '—'}
            </Text>
            <Text style={[s.tdCell, s.tdNum, s.colRate]}>
              {li.unitPrice != null ? fmt(li.unitPrice) : '—'}
            </Text>
            <Text style={[s.tdCell, s.tdNum, s.colAmt]}>{fmt(li.amount)}</Text>
          </View>
        ))}

        {/* Table bottom border */}
        <Svg height={2} width="100%" style={{ marginBottom: 4 }}>
          <Rect x={0} y={0} width="100%" height={2} fill={BORDER} />
        </Svg>

        {/* Totals */}
        <View style={s.totalsSection}>
          <View style={s.totalsBox}>
            {subtotal !== Number(invoice.totalAmt) && (
              <View style={s.totalRow}>
                <Text style={s.tLabel}>Subtotal</Text>
                <Text style={s.tValue}>{fmt(subtotal)}</Text>
              </View>
            )}
            <View style={[s.totalRow, s.totalRowBold]}>
              <Text style={s.tLabelBold}>Total</Text>
              <Text style={s.tValueBold}>{fmt(invoice.totalAmt)}</Text>
            </View>
            {!paid && (
              <View style={[s.totalRow, s.totalRowBalance]}>
                <Text style={s.tLabelBalance}>Balance Due</Text>
                <Text style={s.tValueBalance}>{fmt(invoice.balance)}</Text>
              </View>
            )}
            {paid && (
              <View style={[s.totalRow, s.totalRowPaid]}>
                <Text style={s.tLabelPaid}>Amount Paid</Text>
                <Text style={s.tValuePaid}>{fmt(invoice.totalAmt)}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Memo */}
        {!!invoice.memo && (
          <View style={s.memo}>
            <Text style={s.sectionLabel}>NOTES / MEMO</Text>
            <Text style={s.memoText}>{invoice.memo}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={s.footer}>
          <Text style={s.footerMsg}>Thank you for your business.</Text>
          <Text style={s.footerMeta}>
            {companyName}{invoice.docNumber ? ` · Invoice #${invoice.docNumber}` : ''}
          </Text>
        </View>

      </Page>
    </Document>
  );
}
